// Text operators type for matching (block examples, intent phrases, document text) — text conversion P4, spec §4.6:
// customers' Latin is converted before matching, so a Latin text here matches badly. Warn, never block.
// No imports on purpose: `node --test` loads this file directly (ui/tests/cyrillicCheck.test.ts).

export const MAX_CHECK_LINES = 20;
/** The hub's convert route takes 1–500 characters (backend text-monitor `MAX_TEST_TEXT`). */
export const MAX_PIECE = 500;
export const CYRILLIC_NOTE = "Write this in Cyrillic: customers' Latin is converted before matching";

const LINKS = /https?:\/\/\S+|www\.\S+|\S+@\S+\.\S+/gi;
// The converter leaves a text with any Cyrillic letter as it is, so such a line ("QPay-аар төлнө") is never checked.
const CYRILLIC = /[\u0400-\u04FF]/;

/** Lines with no Cyrillic that still have a Latin letter once links and e-mails are taken out (first MAX_CHECK_LINES). */
export function latinLines(text: string): { index: number; line: string }[] {
  const out: { index: number; line: string }[] = [];
  const lines = text.split('\n');
  for (let index = 0; index < lines.length && out.length < MAX_CHECK_LINES; index++) {
    const line = lines[index];
    if (!CYRILLIC.test(line) && /[A-Za-z]/.test(line.replace(LINKS, ' '))) out.push({ index, line });
  }
  return out;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Swap only the tokens the converter changed, left to right, whole words only — the converter lower-cases its output,
 * so taking its whole line would lower-case the operator's own Cyrillic. Kept (`keep`) words stay.
 */
export function fixLine(line: string, changes: { raw: string; out: string; kind: string }[]): string {
  let text = line;
  let from = 0;
  for (const c of changes) {
    if (c.kind === 'keep' || !c.raw || c.raw === c.out) continue;
    const re = new RegExp(`(?<![\\p{L}\\p{N}])${escape(c.raw)}(?![\\p{L}\\p{N}])`, 'giu');
    re.lastIndex = from;
    const m = re.exec(text);
    if (!m) continue;
    text = text.slice(0, m.index) + c.out + text.slice(m.index + m[0].length);
    from = m.index + c.out.length;
  }
  return text;
}

/** A long line cut at spaces into pieces of at most `max` characters (a longer word is cut where it must be). */
export function checkPieces(line: string, max: number): string[] {
  const pieces: string[] = [];
  let rest = line.trim();
  while (rest.length > max) {
    const cut = rest.lastIndexOf(' ', max);
    const at = cut > 0 ? cut : max;
    pieces.push(rest.slice(0, at));
    rest = rest.slice(at).trim();
  }
  if (rest) pieces.push(rest);
  return pieces;
}

/** Use the Cyrillic for the given lines — only where the line is still exactly what was checked (`from`). */
export function applyLineFixes(text: string, fixes: { index: number; from: string; to: string }[]): string {
  const lines = text.split('\n');
  for (const f of fixes) if (lines[f.index] === f.from) lines[f.index] = f.to;
  return lines.join('\n');
}
